import {z} from "zod"

export type BatchUpdateProperty = "addTagId" | "assignUserId" | "nextActor" | "paymentState" | "removeTagId" | "removeUserAssignment" | "state"
export type BatchUpdateType = (property: BatchUpdateProperty, value: string | boolean) => Promise<number>
/** Per-item outcome of a whole-selection batch action, aligned to the input order. */
export type BatchItemOutcome = "succeeded" | "skipped" | "errored"

export type BatchMenuItem<EntityType extends {id: string; legacyId?: number}> = {
    groupLabel: string
    items: ({
        label: string
        text?: string
        tooltip?: string
    } & (
        | {
              /** Called once per item; `false` counts as skipped, a throw as failed. */
              operation: (item: EntityType) => Promise<boolean>
              batchOperation?: never
          }
        | {
              /**
               * Called once with every item, for actions backed by a single batch mutation. Returns
               * one outcome per item, in input order; the runner counts and refreshes exactly as it
               * does for per-item operations. Returning `{outcomes, summary}` replaces the runner's
               * default snackbar text with `summary`, for actions whose result has more shades than
               * succeeded/skipped/errored.
               */
              batchOperation: (items: EntityType[]) => Promise<BatchItemOutcome[] | {outcomes: BatchItemOutcome[]; summary: string}>
              operation?: never
          }
    ))[]
}

export type ExtraBatchActionItem<EntityType extends {id: string; legacyId?: number}> =
    | BatchMenuItem<EntityType>
    | {groupLabel: string; items: (BatchMenuItem<EntityType> | BatchMenuItem<EntityType>["items"][0])[]}

export function isExtraBatchActionItemWithSubItems<EntityType extends {id: string; legacyId?: number}>(
    item: BatchMenuItem<EntityType> | BatchMenuItem<EntityType>["items"][0],
): boolean {
    return z.object({groupLabel: z.string()}).safeParse(item).success
}

export type BatchDownloadData<EntityType extends {id: string; legacyId?: number}> = {
    pathInMenu: string
    getDataObjectsForDownload: (item: EntityType) => Promise<{path: string; dataObjectLegacyId: number}[]>
}

export type AdvancedAction = {
    label: string
    operation: () => Promise<void> | void
    tooltip?: string
}

export type SortCriterion = {
    label: string
    operation: () => Promise<void> | void
    active?: boolean
}

export const emptyBatchEditInfo = {
    running: false,
    total: 0,
    numSucceeded: 0,
    numSkipped: 0,
    numErrored: 0,
}
